import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import type { FilterOptionOption } from 'react-select';
import { Field } from 'redux-form';

import type { W_Equipment_FindForPurchaseOrderOptions_VM } from 'ab-api/web/equipment/findForPurchaseOrderOptions';

import type AccountOptionVM from 'ab-viewModels/account/option.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';
import type LocationOptionVM from 'ab-viewModels/location/option.viewModel';
import type ToolVM from 'ab-viewModels/tool/option.viewModel';
import type WorkRequestOptionVM from 'ab-viewModels/workRequest/workRequestOption.viewModel';

import { formatDate } from '@acceligentllc/shared/utils/time';

import { RepairStatus } from '@acceligentllc/shared/enums/repairStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';
import Select from 'af-fields/SelectField';
import Textarea from 'af-fields/Textarea';

import * as EquipmentAction from 'af-actions/equipment';
import * as LocationAction from 'af-actions/location';
import * as ToolAction from 'af-actions/tool';
import * as JobAction from 'af-actions/workRequests';

import EquipmentLabel from './EquipmentOptionLabel';
import type FormModel from './formModel';
import StatusLabel from './StatusLabel';
import styles from './styles.module.scss';
import ToolLabel from './ToolOptionLabel';
import { getTimeBetweenDateTimes } from './utils';

interface OwnProps {
	superintendents: EmployeeOptionVM[];
	operators: EmployeeOptionVM[];
	disabled?: boolean;
	initialToolId: Nullable<string>;
	initialEquipmentId: Nullable<string>;
	change: (field: string, value) => void;
	formData: FormModel;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const getSelectOptionValue = (option: { id: number; }) => option.id;
const getSelectAccountOptionValue = (option: { accountId: number; }) => option.accountId;

const formatLocationOptionLabel = (option: LocationOptionVM) => <div>{option.nickname}</div>;
const formatEmployeeOptionLabel = (option: EmployeeOptionVM) => <div>{option.firstName} {option.lastName} ({option.uniqueId})</div>;
const formatJobOptionLabel = (option: WorkRequestOptionVM) => <div>{option.jobCode}</div>;

const filterSelects = (textA?: string, textB?: string) => (textA ?? '').toLowerCase().includes((textB ?? '').toLowerCase());
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterLocations = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(data.nickname, text);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterEmployee = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(`${data.firstName} ${data.lastName} ${data.uniqueId}`, text);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterJob = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(data?.jobCode ?? '', text);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterEquipment = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(`${data.code} ${data.specification}`, text);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterTool = ({ data }: FilterOptionOption<any>, text: string) => filterSelects(`${data.serialNumber} ${data.toolType}`, text);

const LocationSelect = Select as unknown as new () => typeof Select<LocationOptionVM>;
const ToolSelect = Select as unknown as new () => typeof Select<ToolVM>;
const JobSelect = Select as unknown as new () => typeof Select<WorkRequestOptionVM>;
const EquipmentSelect = Select as unknown as new () => typeof Select<W_Equipment_FindForPurchaseOrderOptions_VM>;
const AccountSelect = Select as unknown as new () => typeof Select<AccountOptionVM>;

const DetailsSection = (props: Props) => {
	const {
		superintendents,
		operators,
		findAllTools,
		findAllLocations,
		findAllJobs,
		findAllEquipment,
		disabled,
		initialToolId,
		initialEquipmentId,
		change,
		formData,
	} = props;

	const [locations, setLocations] = React.useState<LocationOptionVM[]>([]);
	const [tools, setTools] = React.useState<ToolVM[]>([]);
	const [jobs, setJobs] = React.useState<WorkRequestOptionVM[]>([]);
	const [equipment, setEquipment] = React.useState<W_Equipment_FindForPurchaseOrderOptions_VM[]>([]);

	const loadStuff = React.useCallback(async () => {

		const [
			_tools,
			_locations,
			_jobs,
			_equipments,
		] = await Promise.all([
			findAllTools(),
			findAllLocations(),
			findAllJobs(),
			findAllEquipment(),
		]);

		setTools(_tools);
		setLocations(_locations);
		setJobs(_jobs);
		setEquipment(_equipments);

		if (initialToolId) {
			const _tool = _tools.find((tool) => tool.id === +initialToolId);
			const _equipment = initialEquipmentId ? _equipments.find((__equipment) => __equipment.id === +initialEquipmentId) : null;
			change('tool', _tool);
			change('equipment', _equipment);
		}
	}, [change, findAllEquipment, findAllJobs, findAllLocations, findAllTools, initialEquipmentId, initialToolId]);

	React.useEffect(() => {
		loadStuff();
	}, [loadStuff]);

	const priceOverrideDisabled = React.useMemo(() => !formData.priceManualOverride || disabled, [disabled, formData.priceManualOverride]);

	const price = React.useMemo(() => {
		return priceOverrideDisabled ? formData.items?.reduce((acc, item) => acc + item.price, 0) ?? 0 : formData.price;
	}, [formData.items, formData.price, priceOverrideDisabled]);

	const totalTimeForRepair = React.useMemo(() => {
		if (!formData.completedAt) {
			return '';
		}

		return getTimeBetweenDateTimes(formData.createdAt, formData.completedAt);
	}, [formData]);

	const equipmentValidForSelect = React.useMemo(() => {
		return equipment.filter((e) => !e.isDeleted);
	}, [equipment]);

	const equipmentOption = React.useMemo(() => {
		return equipment.find((i) => i.id === formData?.equipment?.id);
	}, [equipment, formData?.equipment?.id]);

	const renderSelectedEquipment = React.useCallback(() => {
		if (!equipmentOption) {
			return null;
		}
		return <EquipmentLabel {...equipmentOption} />;
	}, [equipmentOption]);

	return (
		<div className={styles.section}>
			<SegmentLabel
				label="Tool Repair Details"
				tooltip="Repair record, including the tool, reported issue, repair location, assigned personnel, associated job, and the total time and cost of the repair."
			/>
			<div className={styles['field-row']}>
				<Row>
					<Col md={4}>
						<div>Tool Repair Status</div>
						<StatusLabel status={formData.status ?? RepairStatus.NEW} transparent={false} />
					</Col>
					<Col md={4}>
						<div>Date Started</div>
						<div className={styles['field-value']}>
							{
								formData.startedAt
									? formatDate(formData.startedAt, TimeFormat.FULL_DATE_LONG)
									: 'N/A'
							}
						</div>
					</Col>
					<Col md={4}>
						<div>Date Completed</div>
						<div className={styles['field-value']}>
							{
								formData.startedAt
									? formatDate(formData.completedAt, TimeFormat.FULL_DATE_LONG)
									: 'N/A'
							}
						</div>
					</Col>
					<Col md={4}>
						<div>Total Time for Repair</div>
						<div className={styles['field-value']}>
							{totalTimeForRepair}
						</div>
					</Col>
					<Col md={4}>
						{
							priceOverrideDisabled &&
							<div className={styles['total-cost-container']}>
								<div>Total Cost ($)</div>
								<div className={styles['field-value']}>
									$ {price}
								</div>
							</div>
						}
						{
							!priceOverrideDisabled &&
							<Field
								addonAfter="$"
								component={Input}
								defaultValue={price}
								disabled={priceOverrideDisabled}
								label="Total Cost"
								name="price"
								step={0.01}
								type="number"
							/>
						}
					</Col>
					<Col md={4}>

						<Field
							component={Checkbox}
							isDisabled={disabled}
							isStandaloneRow={true}
							label="Manual Override"
							name="priceManualOverride"
						/>
					</Col>
				</Row>
			</div>
			<Row className="row--padded">
				<Col md={6}>
					<Field
						component={ToolSelect}
						filterOption={filterTool}
						formatOptionLabel={ToolLabel}
						getOptionValue={getSelectOptionValue}
						isDisabled={disabled}
						label="Tool *"
						name="tool"
						options={tools}
						placeholder="Select tool"
					/>
					<Field
						component={JobSelect}
						filterOption={filterJob}
						formatOptionLabel={formatJobOptionLabel}
						getOptionValue={getSelectOptionValue}
						isDisabled={disabled}
						label="Job Number"
						name="workRequest"
						options={jobs}
						placeholder="Select Job Number"
					/>
					<Field
						component={AccountSelect}
						filterOption={filterEmployee}
						formatOptionLabel={formatEmployeeOptionLabel}
						getOptionValue={getSelectAccountOptionValue}
						isDisabled={disabled}
						label="Superintendent *"
						name="superintendent"
						options={superintendents}
						placeholder="Select Superintendent"
					/>
				</Col>
				<Col md={6}>
					<Field
						component={LocationSelect}
						filterOption={filterLocations}
						formatOptionLabel={formatLocationOptionLabel}
						getOptionValue={getSelectOptionValue}
						isDisabled={disabled}
						label="Repair Location *"
						name="location"
						options={locations}
						placeholder="Select Repair Location"
					/>
					<Field
						component={EquipmentSelect}
						filterOption={filterEquipment}
						formatOptionLabel={EquipmentLabel}
						getOptionValue={getSelectOptionValue}
						isDisabled={disabled}
						label="Equipment"
						name="equipment"
						options={equipmentValidForSelect}
						placeholder="Select Equipment"
						renderSelected={renderSelectedEquipment}
					/>
					<Field
						component={AccountSelect}
						filterOption={filterEmployee}
						formatOptionLabel={formatEmployeeOptionLabel}
						getOptionValue={getSelectAccountOptionValue}
						isDisabled={disabled}
						label="Operator *"
						name="operator"
						options={operators}
						placeholder="Select Operator"
					/>
				</Col>
				<Col md={12}>
					<Field
						component={Textarea}
						controlCursor={true}
						disabled={disabled}
						label="Reported Issue *"
						name="reportedIssue"
						rows={6}
					/>
				</Col>
			</Row>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findAllTools: ToolAction.findAllForList,
		findAllLocations: LocationAction.findList,
		findAllJobs: JobAction.findWorkRequestOptionsForPurchaseOrder,
		findAllEquipment: EquipmentAction.findForPurchaseOrderOptions,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(DetailsSection);
